import React, { useId, forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import ReactFocusLock, { FreeFocusInside } from 'react-focus-lock'
import { FieldErrors, useForm } from 'react-hook-form'
import { useAppDispatch } from 'reduxTyped'
import { z } from 'zod'
import { l, BtnV2, Modal, Icon } from '@manychat/manyui'

import { mapExitActionToMessageForm } from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/lib/mappers'
import {
  SubmitRef,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { HookFormTextArea } from 'components/HookFormControls/HookFormTextArea/HookFormTextArea'
import { HookFormTextInput } from 'components/HookFormControls/HookFormTextInput/HookFormTextInput'
import { ExitActionType, LeadQualExitAction } from 'shared/api/requests/aiEngagementGoal/schemas'

import { ExitActionProps } from '../../lib/types'

const schema = z.discriminatedUnion('withButton', [
  z.object({
    withButton: z.literal(false),
    message: z
      .string()
      .min(1, l.translate('Enter message to continue'))
      .max(640, l.translate('Message must be {count} characters or fewer', { count: 640 })),
    title: z.string().optional(),
    url: z.string().optional(),
  }),
  z.object({
    withButton: z.literal(true),
    message: z
      .string()
      .min(1, l.translate('Enter message to continue'))
      .max(640, l.translate('Message must be {count} characters or fewer', { count: 640 })),
    title: z
      .string()
      .min(1, l.translate('Enter button title to continue'))
      .max(20, l.translate('Button title must be {count} characters or fewer', { count: 20 })),
    url: z.string().min(1, l.translate('Enter link to continue')).url(l.translate('Invalid link')),
  }),
])

const buildButton = (values: z.infer<typeof schema>) => {
  if (!values.withButton) {
    return undefined
  }

  return { title: values.title, url: values.url }
}

export const SendMessageAction = forwardRef<SubmitRef<LeadQualExitAction>, ExitActionProps>(
  ({ exitActionData, isActive }, ref) => {
    const dispatch = useAppDispatch()

    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
    const formId = useId()

    const defaultValues = {
      ...mapExitActionToMessageForm(exitActionData),
      withButton: Boolean(
        exitActionData?.type === ExitActionType.SEND_MESSAGE && exitActionData.data.message.button,
      ),
    }

    const { handleSubmit, control, watch, setValue, formState } = useForm({
      defaultValues,
      resolver: zodResolver(schema),
    })

    const { withButton, title, url } = watch()

    const onLinkValidationError = (
      errors: FieldErrors<{
        message: string
        title: string
        link: string
        withButton: boolean
      }>,
    ) => {
      if (!errors.title && !errors.link) {
        setIsLinkModalOpen(false)
      } else {
        setValue('withButton', false)
      }
    }

    const onSaveLink = () => {
      setValue('withButton', true)
      handleSubmit(() => setIsLinkModalOpen(false), onLinkValidationError)()
    }

    const onDeleteLink = () => {
      setValue('withButton', false)
      setValue('title', '')
      setValue('url', '')
      setIsLinkModalOpen(false)
    }

    const onCloseLinkModal = () => {
      if (!withButton) {
        setValue('title', '')
        setValue('url', '')
      }

      setIsLinkModalOpen(false)
    }

    useEffect(() => {
      if (isActive) {
        dispatch(
          AiAutomationLayoutActions.showConfirmChanged(
            formState.isDirty || exitActionData?.type !== ExitActionType.SEND_MESSAGE,
          ),
        )
      } else {
        dispatch(AiAutomationLayoutActions.showConfirmChanged(false))
      }
    }, [dispatch, exitActionData?.type, formState.isDirty, isActive])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let result: SubmitResult<LeadQualExitAction> = { isValid: false }

          await handleSubmit((data) => {
            result = {
              isValid: true,
              data: {
                type: ExitActionType.SEND_MESSAGE,
                data: {
                  message: {
                    text: data.message,
                    button: buildButton(data),
                  },
                },
              },
            }
          })()
          return result
        },
      }),
      [handleSubmit],
    )

    return (
      <div>
        <form id={formId}>
          <HookFormTextArea
            control={control}
            disabled={!isActive}
            name="message"
            ariaLabelledBy="leadgen-context"
            placeholder={l.translate('Write a message')}
            minRows={2}
            maxRows={6}
          />

          {!withButton && (
            <BtnV2
              className="m-t-sm"
              variant="default"
              leftIcon="Plus"
              fullWidth
              disabled={!isActive}
              onClick={() => setIsLinkModalOpen(true)}
            >
              {l.translate('Add a link')}
            </BtnV2>
          )}

          {withButton && (
            <div
              className={cx('d-flex m-t justify-between align-center b-a rounded p-y-xxs p-x-sm', {
                ['c-pointer bg-white']: isActive,
                [`c-not-allowed`]: !isActive,
              })}
              onClick={() => isActive && setIsLinkModalOpen(true)}
              data-title={url}
            >
              <span className="text-ellipsis">{title}</span>
              <Icon.Link className="text-primary flex-shrink-0" />
            </div>
          )}

          <ReactFocusLock disabled={!isLinkModalOpen} returnFocus>
            <FreeFocusInside>
              <Modal
                title={l.translate('Add a link')}
                width={460}
                open={isLinkModalOpen}
                buttons={[
                  {
                    onClick: onDeleteLink,
                    variant: 'danger-light',
                    label: l.translate('Delete link'),
                  },
                  {
                    onClick: onSaveLink,
                    variant: 'primary',
                    label: l.translate('Save'),
                  },
                ]}
                onRequestClose={onCloseLinkModal}
              >
                <HookFormTextInput
                  control={control}
                  name="title"
                  label={l.translate('Button label')}
                  ariaLabel={l.translate('Button label')}
                  placeholder={l.translate('Add a button label e.g Open')}
                />
                <HookFormTextInput
                  className="m-t"
                  control={control}
                  name="url"
                  label={l.translate('<l10n context="Noun"/>Link')}
                  placeholder={l.translate('Add a button link e.g https://manychat.com')}
                />
              </Modal>
            </FreeFocusInside>
          </ReactFocusLock>
        </form>
      </div>
    )
  },
)
